<template>
  <div v-if="isVisibleSource || isVisibleTarget">
    <div
      @mouseenter="handleMouseEnter"
      @mouseleave="showMenu = false"
      class="box"
    >
      <div class="bubble-container">
        <div class="my-chat-bubble__time">
          <p class="smaller dim">{{ formattedTime }}</p>
          <GeneralMessageChatGoingDmActions
            v-if="isDmMessage"
            :message="message"
          />
          <template v-else>
            <GeneralMessageChatGoingActions
              v-if="isVisibleTarget"
              :is-downloading="isDownloadingTarget"
              :show-loading="!isReceivedAudio"
              :is-playing-audio="isTargetPlaying"
              :is-top="true"
              :message-contents="message.tgt.contents"
              :play-audio="playTargetAudio"
              :is-last-is-own
              :is-target="true"
              :message
            />
            <GeneralMessageChatGoingActions
              v-if="isVisibleSource"
              :is-visible-button-copy="showMenu"
              :is-downloading="isDownloadingSource"
              :show-loading="!isReceivedAudio"
              :is-playing-audio="isSourcePlaying"
              :is-top="false"
              :message-contents="message.src.contents"
              :play-audio="playSourceAudio"
              :is-last-is-own
              :is-target="false"
              :message
            />
          </template>
        </div>

        <GeneralMessageChatGoingText
          :chat-message-call="message"
          :is-source-playing
          :is-target-playing
          :is-visible-source
          :is-visible-target
          :is-dm-message
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, provide, ref, toRef, watch } from 'vue';

import GeneralMessageChatGoingActions from '@/components/domains/chat/chat-general/GeneralMessageChatGoingActions.vue';
import GeneralMessageChatGoingDmActions from '@/components/domains/chat/chat-general/GeneralMessageChatGoingDmActions.vue';
import GeneralMessageChatGoingText from '@/components/domains/chat/chat-general/GeneralMessageChatGoingText.vue';
import { useMessageBubbleCommon } from '@/composables/domains/chat/useMessageBubbleCommon';
import { useAudioStore } from '@/stores/audio';
import { useUserStore } from '@/stores/user';
import { ChatMessage } from '@/types/socket';
import { getBrowserAndDeviceInfo } from '@/utils/device';
import {
  ij_callLogId,
  ij_isLastIsOwn,
  ij_isLastMessage,
  ij_setEmptyBubbleMy,
  ij_isOldMessage,
} from '@/utils/injectionKeys.js';
import { findIndexReverse } from '@/utils/shared/utilsArray';

const props = defineProps<{
  messages: ChatMessage[];
  message: ChatMessage;
  lastCallLogId: string;
}>();

const setEmptyBubbleMy = inject(ij_setEmptyBubbleMy);

const audioStore = useAudioStore();
const userStore = useUserStore();

//시간 포멧
const sentAt = new Date(props.message.sentAt);
const hours = sentAt.getHours();
const minutes = sentAt.getMinutes();
const period = hours >= 12 ? 'pm' : 'am';
const formattedHours = hours % 12 || 12; // Convert 0 to 12 for 12-hour format
const formattedMinutes = minutes.toString().padStart(2, '0');
const formattedTime = `${formattedHours}:${formattedMinutes} ${period}`;

const isHidden = ref(false);
const isSourcePlaying = ref(false);
const isTargetPlaying = ref(false);
const showMenu = ref(false);

const { isEmptyTgt, isEmptySrc, handleChangeReset } = useMessageBubbleCommon({
  chatMessage: toRef(props, 'message'),
  isHidden,
  setEmptyBubble: setEmptyBubbleMy,
});

const isDmMessage = computed<boolean>(() => {
  const { flag = '' } = props.message;
  return flag === 'mt';
});

const isReceivedAudio = computed(() => {
  if (!isLastIsOwn.value) return true;
  const isAudioComplete = props.message.isAudioComplete === true;
  const isReset = props.message.reset === '1';
  return isAudioComplete || isReset;
});

const isDownloadingSource = computed(
  () =>
    audioStore.audioDownloading === props.message.callLogId &&
    isSourcePlaying.value
);

const isDownloadingTarget = computed(
  () =>
    audioStore.audioDownloading === props.message.callLogId &&
    isTargetPlaying.value
);

// 나의 발화 중 이 메세지가 최신인지
const isLastIsOwn = computed(() => {
  const messageLastIsOwn = props.messages.findLast((item) => item.isOwn);
  return messageLastIsOwn!.callLogId === props.message.callLogId;
});

// 상대 발화 중 이 메세지가 마지막인지
const isLastMessage = computed(
  () => props.messages.at(-1)!.callLogId === props.message?.callLogId
);

// 상대 발화 중 이 메세지가 마지막인지
const isOldMessage = computed(() => {
  if (props.messages.length < 3) return false;
  const indexReverse = findIndexReverse(
    props.messages,
    (item) => item.callLogId === props.message?.callLogId
  );
  if (indexReverse === null) return false;
  return indexReverse < -2;
});

// 원문 보임 여부
const isVisibleSource = computed(() => {
  return !isEmptySrc.value;
});

// 번역문 보임 여부
const isVisibleTarget = computed(() => {
  const isSingleLanguage = audioStore?.isSingleLanguage; // 단일 언어인가
  const isActiveMyLanguageOnly =
    userStore.userPreferences.isActiveMyLanguageOnly; // 나의 언어만 보기 활성화 여부

  return !isEmptyTgt.value && (isSingleLanguage || !isActiveMyLanguageOnly);
});

const handleMouseEnter = () => {
  const { os } = getBrowserAndDeviceInfo();
  if (os === 'iOS') return;
  showMenu.value = true;
};

// 원문 오디오 재생
const playSourceAudio = () => {
  if (isSourcePlaying.value === true) {
    //ui 즉시 변경(중복 요청 방지)
    isSourcePlaying.value = false;
    audioStore.setPlayState(0, props.message.callLogId); //재생 중이면 정지
    return;
  }

  if (props.message.srcAudioKeys) {
    const { callLogId, srcAudioKeys } = props.message;
    audioStore.setAudioManulSrcs(false, callLogId, srcAudioKeys);
  }
};

// 번역문 오디오 재생
const playTargetAudio = () => {
  if (isTargetPlaying.value === true) {
    isTargetPlaying.value = false;
    audioStore.setPlayState(0, props.message.callLogId);
    return;
  }
  if (props.message.tgtAudioKeys) {
    const { callLogId, tgtAudioKeys } = props.message;
    audioStore.setAudioManulSrcs(true, callLogId, tgtAudioKeys);
  }
};

const resetIcons = () => {
  isSourcePlaying.value = false;
  isTargetPlaying.value = false;
};

watch(
  () => props.message.reset,
  (resetNew) => handleChangeReset(resetNew),
  { immediate: true }
);

watch(
  () => audioStore.isSoundOn,
  (newVal) => {
    if (!newVal) resetIcons();
  }
);

//재생 ui표시
watch(
  () => [audioStore.playState, audioStore.playCallLogId],
  ([newVal1, newVal2]) => {
    if (newVal1 == 1) {
      if (newVal2 === props.message.callLogId) {
        isSourcePlaying.value = true;
        isTargetPlaying.value = false;
      } else {
        resetIcons();
      }
    } else if (newVal1 == 2) {
      if (newVal2 === props.message.callLogId) {
        isSourcePlaying.value = false;
        isTargetPlaying.value = true;
      } else {
        resetIcons();
      }
    } else {
      resetIcons();
    }
  }
);

provide(ij_callLogId, props.message.callLogId);
provide(ij_isLastIsOwn, isLastIsOwn);
provide(ij_isLastMessage, isLastMessage);
provide(ij_isOldMessage, isOldMessage);
</script>

<style scoped lang="scss">
.hidden {
  visibility: hidden;
}

.loading {
  width: fit-content;
  align-self: flex-end;
  margin-bottom: 0.75rem;
  padding: 0.25rem 1.5rem 0.75rem 1rem;
  border-radius: 8px;
}

.box {
  display: flex;
  padding: 0 1rem 1rem 1rem;
  position: relative;
  min-height: unset;
}

.box.last-message {
  min-height: 40dvh;
}

.bubble-container {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  height: fit-content;
}

.my-chat-bubble__time {
  display: flex;
  flex-direction: column-reverse;
  align-items: flex-end;
  white-space: nowrap;
  position: relative;
}
</style>
